import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

let id = 0;
const nextId = () => ++id;

const KIND_STYLES = {
  error:   { bar: 'bg-rose-500',    icon: XCircle,       text: 'text-rose-400',    bg: 'bg-rose-500/8 border-rose-500/20' },
  success: { bar: 'bg-emerald-500', icon: CheckCircle2,  text: 'text-emerald-400', bg: 'bg-emerald-500/8 border-emerald-500/20' },
  info:    { bar: 'bg-violet-500',  icon: Info,          text: 'text-violet-400',  bg: 'bg-violet-500/8 border-violet-500/20' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((toastId) => {
    setToasts((cur) => cur.filter((t) => t.id !== toastId));
  }, []);

  const push = useCallback((toast) => {
    const toastId = nextId();
    const item = { id: toastId, kind: 'info', ttl: 4000, ...toast };
    setToasts((cur) => [...cur, item]);
    if (item.ttl > 0) setTimeout(() => dismiss(toastId), item.ttl);
    return toastId;
  }, [dismiss]);

  const value = useMemo(() => ({
    toasts, dismiss, push,
    info:    (m, t) => push({ kind: 'info',    message: m, title: t }),
    success: (m, t) => push({ kind: 'success', message: m, title: t }),
    error:   (m, t) => push({ kind: 'error',   message: m, title: t }),
  }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[360px] flex-col gap-2">
        {toasts.map((t) => {
          const style = KIND_STYLES[t.kind] || KIND_STYLES.info;
          const Icon = style.icon;
          return (
            <div
              key={t.id}
              className={`pointer-events-auto animate-scale-in flex items-start gap-3 overflow-hidden rounded-xl border shadow-xl backdrop-blur-md ${style.bg}`}
            >
              {/* Left accent bar */}
              <div className={`w-1 self-stretch shrink-0 ${style.bar} rounded-l-xl`} />
              {/* Icon */}
              <div className={`mt-3 shrink-0 ${style.text}`}>
                <Icon size={16} />
              </div>
              {/* Text */}
              <div className="flex-1 py-3 pr-1 text-sm">
                {t.title && <div className="font-semibold text-zinc-100 mb-0.5">{t.title}</div>}
                <div className="text-zinc-300 leading-snug">{t.message}</div>
              </div>
              {/* Dismiss */}
              <button
                onClick={() => dismiss(t.id)}
                className="mt-3 mr-3 shrink-0 rounded p-0.5 text-zinc-500 hover:text-zinc-200 transition-colors"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
